
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Notification, NotificationType, UserProfile } from '@/types';
import { useAuth } from './AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  sendEmailNotification: (userId: string, title: string, message: string, type: NotificationType) => Promise<void>;
  sendPushNotification: (userId: string, title: string, message: string, type: NotificationType) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user, profile } = useAuth();
  const { toast } = useToast();
  
  // Load user's notifications on auth change
  useEffect(() => {
    if (user) {
      fetchNotifications();
      
      // Set up subscription for real-time notifications
      const subscription = supabase
        .channel('notification_changes')
        .on('postgres_changes', { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'notifications',
          filter: `userid=eq.${user.id}`
        }, (payload) => {
          const newNotification = payload.new as unknown as Notification;
          setNotifications(prev => [newNotification, ...prev]);
          
          // Show toast for new notifications
          toast({
            title: newNotification.title,
            description: newNotification.message,
          });
        })
        .subscribe();
        
      return () => {
        supabase.removeChannel(subscription);
      };
    }
  }, [user, toast]);
  
  // Fetch notifications from database
  const fetchNotifications = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('userid', user.id)
      .order('createdat', { ascending: false })
      .limit(50);
      
    if (!error && data) {
      setNotifications(data as unknown as Notification[]);
    }
  };
  
  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Mark notification as read
  const markAsRead = async (id: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
      .eq('userid', user.id);
      
    if (!error) {
      setNotifications(prev => 
        prev.map(item => 
          item.id === id ? { ...item, read: true } : item
        )
      );
    }
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;
    
    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('userid', user.id)
      .eq('read', false);
      
    if (!error) {
      setNotifications(prev => 
        prev.map(item => ({ ...item, read: true }))
      );
    }
  };
  
  // Delete a notification
  const deleteNotification = async (id: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', id)
      .eq('userid', user.id);
      
    if (!error) {
      setNotifications(prev => prev.filter(item => item.id !== id));
    }
  };
  
  // Send email notification (would connect to a Supabase Edge Function in production)
  const sendEmailNotification = async (userId: string, title: string, message: string, type: NotificationType) => {
    // In a production app, this would call a Supabase Edge Function to send emails
    // For now, we'll just create a notification record
    const { error } = await supabase
      .from('notifications')
      .insert({
        userid: userId,
        title,
        message,
        type,
        read: false,
        createdat: new Date().toISOString()
      });
      
    if (error) {
      console.error('Error sending email notification:', error);
    }
  };
  
  // Send push notification (would connect to a push notification service in production)
  const sendPushNotification = async (userId: string, title: string, message: string, type: NotificationType) => {
    // In a production app, this would call a service like Firebase Cloud Messaging
    // For now, we'll just create a notification record with a note that it's a push
    const { error } = await supabase
      .from('notifications')
      .insert({
        userid: userId,
        title: `${title} (Push)`,
        message,
        type,
        read: false,
        createdat: new Date().toISOString()
      });
      
    if (error) {
      console.error('Error sending push notification:', error);
    }
  };
  
  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      sendEmailNotification,
      sendPushNotification
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
